<script setup>
import { ref, onMounted, onUnmounted, watch, shallowRef } from 'vue'
import { Chart, registerables } from 'chart.js'
import { useTheme } from '@/utils/composables/useTheme'

Chart.register(...registerables)

const { isDark } = useTheme()

const props = defineProps({
  data: {
    type: Object,
    required: true
  },
  centerLabel: {
    type: String,
    default: 'Roles'
  }
})

const chartCanvas = ref(null)
const chartInstance = shallowRef(null)

const renderChart = () => {
    if (chartInstance.value) {
        chartInstance.value.destroy()
        chartInstance.value = null
    }
    
    // Custom plugin to draw text in center of donut
    const centerText = {
        id: 'centerText',
        beforeDraw: function(chart) {
            if (chart.config.type !== 'doughnut') return;
            var height = chart.height,
            ctx = chart.ctx;
            
            ctx.save();
            var fontSize = (height / 114).toFixed(2);
            ctx.font = "bold " + fontSize + "em sans-serif";
            ctx.textBaseline = "middle";
            ctx.fillStyle = isDark.value ? "#e8edf5" : "#000";
            
            // Text 1: Value
            var text = chart.config.data.datasets[0].data.reduce((a, b) => a + b, 0);

            // If checking percentages, maybe just show number of slices? 
            // For now let's assume we pass counts.
            if (props.data.datasets[0].label === 'Percentage') {
                text = '100%'; 
            }
            
            // New Centering Logic using textAlign and chartArea to account for legend
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            
            // Calculate center based on the drawing area, not the full canvas width
            const { top, left, bottom, right } = chart.chartArea || {};
            if (top === undefined) return; // Safety check

            const centerX = (left + right) / 2;
            const centerY = (top + bottom) / 2;
            
            // Adjust vertical positions slightly to split the space
            var textY = centerY - ((bottom - top) * 0.05); // Move up slightly
            
            ctx.fillText(text, centerX, textY);

            // Text 2: Label
            var fontSize2 = (height / 200).toFixed(2);
             ctx.font = fontSize2 + "em sans-serif";
             ctx.fillStyle = isDark.value ? "#9aadc4" : "#6b7280";
             var text2 = props.centerLabel;
             
             // Move down for the second line
             var textY2 = centerY + ((bottom - top) * 0.08); // Move down
                 
             ctx.fillText(text2, centerX, textY2);
             
            ctx.restore();
        }
    };

    if (chartCanvas.value) {
        chartInstance.value = new Chart(chartCanvas.value, {
            type: 'doughnut',
            data: props.data,
            plugins: [centerText],
            options: {
                responsive: true,
                maintainAspectRatio: false,
                cutout: '75%',
                plugins: {
                    legend: {
                        position: 'right',
                        labels: {
                            usePointStyle: true,
                             pointStyle: 'circle',
                             padding: 20,
                             font: {
                                 size: 13
                             },
                             color: isDark.value ? '#e8edf5' : '#6b7280'
                        }
                    },
                     tooltip: {
                         callbacks: {
                             label: function(context) {
                                 let label = context.label || '';
                                 if (label) {
                                     label += ': ';
                                 }
                                 if (context.parsed.y !== null) {
                                     label += context.parsed;
                                 } else {
                                     label += context.raw;
                                 }
                                 
                                 // Add % if available in dataset
                                 const dataset = context.chart.data.datasets[context.datasetIndex];
                                 if (dataset.percentages && dataset.percentages[context.dataIndex] !== undefined) {
                                     label += ` (${dataset.percentages[context.dataIndex]}%)`;
                                 } else if (props.data.datasets[0].label === 'Percentage') {
                                     label += '%'
                                 }
                                 return label;
                             }
                         }
                     }
                }
            }
        })
    }
}

watch(() => props.data, () => {
    renderChart()
}, { deep: true })

watch(isDark, () => {
    renderChart()
})

onMounted(() => {
    renderChart()
})

onUnmounted(() => {
    if (chartInstance.value) {
        chartInstance.value.destroy()
        chartInstance.value = null
    }
})
</script>

<template>
  <div class="chart-wrapper">
    <canvas ref="chartCanvas"></canvas>
  </div>
</template>

<style scoped>
.chart-wrapper {
  height: 100%;
  width: 100%;
  min-height: 200px;
}
</style>
